/**
 * Doctor — diagnostic checks for agent-contracts-runtime configuration health
 *
 * Each check returns a DoctorCheck with pass/fail/warn status.
 * The runner collects all checks and determines overall health.
 */

import { existsSync } from "node:fs";
import { readFile } from "node:fs/promises";
import { resolve, join } from "node:path";
import YAML from "yaml";
import { loadConfig, RuntimeConfigSchema, type ResolvedConfig } from "../generator/config.js";
import { checkFreshness } from "../generator/index.js";

export interface DoctorCheck {
  name: string;
  status: "pass" | "fail" | "warn";
  message?: string;
}

export interface DoctorResult {
  checks: DoctorCheck[];
  all_passed: boolean;
}

const ADAPTER_ENV_VARS: Record<string, string> = {
  claude: "ANTHROPIC_API_KEY",
  openai: "OPENAI_API_KEY",
};

async function checkConfigExists(configPath: string): Promise<{ check: DoctorCheck; config?: ResolvedConfig }> {
  try {
    const config = await loadConfig(configPath);
    return {
      check: { name: "config_exists", status: "pass", message: `Loaded ${configPath}` },
      config,
    };
  } catch (err) {
    const absPath = resolve(configPath);
    if (!existsSync(absPath)) {
      return {
        check: { name: "config_exists", status: "fail", message: `Config file not found: ${absPath}` },
      };
    }
    return {
      check: { name: "config_exists", status: "fail", message: `Config parse error: ${(err as Error).message}` },
    };
  }
}

async function checkDslExists(config: ResolvedConfig): Promise<DoctorCheck> {
  if (!existsSync(config.dslPath)) {
    return { name: "dsl_exists", status: "fail", message: `DSL file not found: ${config.dslPath}` };
  }

  try {
    const raw = await readFile(config.dslPath, "utf8");
    const parsed = YAML.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return { name: "dsl_exists", status: "fail", message: "DSL file is empty or not a valid YAML object" };
    }
    const agentCount = Object.keys(parsed.agents ?? {}).length;
    const taskCount = Object.keys(parsed.tasks ?? {}).length;
    const workflowCount = Object.keys(parsed.workflow ?? {}).length;
    return {
      name: "dsl_exists",
      status: "pass",
      message: `Parsed OK — ${agentCount} agent(s), ${taskCount} task(s), ${workflowCount} workflow(s)`,
    };
  } catch (err) {
    return { name: "dsl_exists", status: "fail", message: `DSL parse error: ${(err as Error).message}` };
  }
}

async function checkManifestFresh(configPath: string, config: ResolvedConfig): Promise<DoctorCheck> {
  const manifestPath = join(config.generatedDir, ".manifest.json");

  if (!existsSync(manifestPath)) {
    return {
      name: "manifest_fresh",
      status: "fail",
      message: "Generated manifest not found. Run `agent-runtime generate` first.",
    };
  }

  try {
    const fresh = await checkFreshness(configPath);
    if (fresh) {
      return { name: "manifest_fresh", status: "pass", message: "Generated files are up to date" };
    }
    return {
      name: "manifest_fresh",
      status: "fail",
      message: "Generated files are stale. Run `agent-runtime generate` to update.",
    };
  } catch (err) {
    return { name: "manifest_fresh", status: "fail", message: `Freshness check error: ${(err as Error).message}` };
  }
}

function checkAdapterConfigured(): DoctorCheck {
  const configured: string[] = [];
  const missing: string[] = [];

  for (const [adapter, envVar] of Object.entries(ADAPTER_ENV_VARS)) {
    if (process.env[envVar]) {
      configured.push(adapter);
    } else {
      missing.push(`${adapter} (${envVar})`);
    }
  }

  if (configured.length > 0) {
    return {
      name: "adapter_configured",
      status: "pass",
      message: `Configured: ${configured.join(", ")}${missing.length > 0 ? ` | Not set: ${missing.join(", ")}` : ""}`,
    };
  }

  return {
    name: "adapter_configured",
    status: "warn",
    message: `No SDK adapter API keys found. Set one of: ${Object.entries(ADAPTER_ENV_VARS).map(([a, e]) => `${e} (${a})`).join(", ")}`,
  };
}

async function checkTsxAvailable(): Promise<DoctorCheck> {
  try {
    await import("tsx/esm");
    return { name: "tsx_available", status: "pass", message: "tsx is installed" };
  } catch {
    return {
      name: "tsx_available",
      status: "fail",
      message: "tsx is not installed. Required for loading generated .ts files. Run: npm install tsx",
    };
  }
}

function checkPluginEntrypoints(config: ResolvedConfig): DoctorCheck {
  if (config.pluginPaths.length === 0) {
    return { name: "plugin_entrypoint", status: "pass", message: "No plugins configured" };
  }

  const missing: string[] = [];
  for (const p of config.pluginPaths) {
    const absPath = resolve(config.configDir, p);
    if (!existsSync(absPath)) {
      missing.push(p);
    }
  }

  if (missing.length === 0) {
    return {
      name: "plugin_entrypoint",
      status: "pass",
      message: `All ${config.pluginPaths.length} plugin file(s) found`,
    };
  }

  return {
    name: "plugin_entrypoint",
    status: "fail",
    message: `Missing plugin file(s): ${missing.join(", ")}`,
  };
}

async function checkBindingsValid(config: ResolvedConfig): Promise<DoctorCheck> {
  if (config.bindingPaths.length === 0) {
    return { name: "bindings_valid", status: "pass", message: "No bindings configured" };
  }

  const errors: string[] = [];
  for (const bp of config.bindingPaths) {
    if (!existsSync(bp)) {
      errors.push(`Not found: ${bp}`);
      continue;
    }
    try {
      const raw = await readFile(bp, "utf8");
      const parsed = YAML.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        errors.push(`Invalid YAML object: ${bp}`);
      }
    } catch (err) {
      errors.push(`Parse error in ${bp}: ${(err as Error).message}`);
    }
  }

  if (errors.length === 0) {
    return {
      name: "bindings_valid",
      status: "pass",
      message: `All ${config.bindingPaths.length} binding file(s) valid`,
    };
  }

  return {
    name: "bindings_valid",
    status: "fail",
    message: errors.join("; "),
  };
}

function checkLogDirectory(config: ResolvedConfig): DoctorCheck {
  const pl = config.resolvedLogging.progressLog;

  if (pl.destination === "none" || pl.destination === "stderr") {
    return { name: "log_directory", status: "pass", message: `Progress log destination: ${pl.destination}` };
  }

  if (!pl.file) {
    return {
      name: "log_directory",
      status: "fail",
      message: "logging.progress_log.file is required when destination is 'file' or 'both'",
    };
  }

  const dir = join(pl.file, "..");
  if (!existsSync(dir)) {
    return {
      name: "log_directory",
      status: "warn",
      message: `Log directory does not exist: ${dir} (will be created on first run)`,
    };
  }

  return { name: "log_directory", status: "pass", message: `Log file: ${pl.file} (naming: ${pl.naming})` };
}

export async function runDoctor(configPath: string): Promise<DoctorResult> {
  const checks: DoctorCheck[] = [];

  // 1. Config exists and parses
  const configResult = await checkConfigExists(configPath);
  checks.push(configResult.check);

  if (!configResult.config) {
    return { checks, all_passed: false };
  }

  const config = configResult.config;

  // 2–7 can run concurrently since they only read state
  const [dslCheck, manifestCheck, pluginCheck, bindingsCheck, tsxCheck] = await Promise.all([
    checkDslExists(config),
    checkManifestFresh(configPath, config),
    Promise.resolve(checkPluginEntrypoints(config)),
    checkBindingsValid(config),
    checkTsxAvailable(),
  ]);

  checks.push(dslCheck);
  checks.push(manifestCheck);
  checks.push(checkAdapterConfigured());
  checks.push(pluginCheck);
  checks.push(bindingsCheck);
  checks.push(tsxCheck);
  checks.push(checkLogDirectory(config));

  const all_passed = checks.every((c) => c.status !== "fail");

  return { checks, all_passed };
}
